import { Box, Stepper, Step, StepLabel, Typography } from '@mui/material';
import { CheckCircle, SkipNext } from '@mui/icons-material';
import { ChecklistPhase, InvestmentChecklistDTO } from '@inversor-hub/types';
import { PHASE_ORDER, PHASE_CONFIG } from './checklist-constants';
import { calculatePhaseProgress, isPhaseSkipped } from './checklist-utils';

interface ChecklistProgressStepperProps {
  checklist: InvestmentChecklistDTO;
  onPhaseClick?: (phase: string) => void;
  selectedPhase?: ChecklistPhase;
}

const ChecklistProgressStepper = ({
  checklist,
  onPhaseClick,
  selectedPhase,
}: ChecklistProgressStepperProps) => {
  const getActiveStep = () => {
    if (selectedPhase) {
      return PHASE_ORDER.indexOf(selectedPhase);
    }
    return 0;
  };

  const isPhaseCompleted = (phase: ChecklistPhase) => {
    const progress = calculatePhaseProgress(checklist.tasks, phase);
    return progress.percentage === 100;
  };

  return (
    <Box
      mb={4}
      sx={{
        width: '100%',
        overflowX: { xs: 'auto', md: 'visible' },
        pb: { xs: 1, md: 0 },
      }}
    >
      <Stepper
        activeStep={getActiveStep()}
        alternativeLabel
        sx={{
          minWidth: { xs: 720, md: 'auto' },
          '& .MuiStepLabel-label': { mt: 1 },
        }}
      >
        {PHASE_ORDER.map((phase) => {
          const config = PHASE_CONFIG[phase];
          const progress = calculatePhaseProgress(checklist.tasks, phase);
          const Icon = config.icon;
          const completed = isPhaseCompleted(phase);
          const skipped = isPhaseSkipped(checklist.tasks, phase);
          const isSelected = phase === selectedPhase;

          return (
            <Step
              key={phase}
              completed={completed}
              sx={{
                cursor: onPhaseClick ? 'pointer' : 'default',
                '& .MuiStepLabel-root': {
                  cursor: onPhaseClick ? 'pointer' : 'default',
                },
              }}
              onClick={() => onPhaseClick?.(phase)}
            >
              <StepLabel
                StepIconComponent={() => (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: { xs: 48, sm: 56 },
                      height: { xs: 48, sm: 56 },
                      borderRadius: '50%',
                      bgcolor: skipped
                        ? 'grey.400'
                        : completed
                          ? config.color
                          : isSelected
                            ? `${config.color}40`
                            : 'grey.200',
                      color: skipped
                        ? 'white'
                        : completed
                          ? 'white'
                          : isSelected
                            ? config.color
                            : 'grey.500',
                      transition: 'all 0.3s',
                      border: isSelected ? `3px solid ${config.color}` : 'none',
                      transform: isSelected ? 'scale(1.06)' : 'scale(1)',
                      boxShadow: isSelected ? 3 : 0,
                      '&:hover': {
                        transform: 'scale(1.06)',
                        boxShadow: 3,
                      },
                    }}
                  >
                    {skipped ? (
                      <SkipNext sx={{ fontSize: { xs: 26, sm: 32 } }} />
                    ) : completed ? (
                      <CheckCircle sx={{ fontSize: { xs: 26, sm: 32 } }} />
                    ) : (
                      <Icon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                    )}
                  </Box>
                )}
              >
                <Box mt={1}>
                  <Typography
                    variant="body2"
                    fontWeight={isSelected ? 700 : 600}
                    color={skipped ? 'text.secondary' : isSelected ? config.color : 'text.primary'}
                  >
                    {config.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {skipped ? 'Omitida' : `${progress.completed}/${progress.total} tareas`}
                  </Typography>
                </Box>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default ChecklistProgressStepper;
